import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Loader2, UserPlus, X, Clock, LogOut } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/* Roster picker for team clock-in / clock-out (Aug 2026).
   Clock-in: all present members checked; already-on-clock members badged and
   skipped; supervisors can pull any company worker onto the shift as a temp
   (mutual coverage) without changing the roster.
   Clock-out: members (and temps) currently on the clock, checked by default. */
export const TeamRosterDialog = ({ open, onClose, mode, targetId, targetName, token, busy, onConfirm }) => {
  const [members, setMembers] = useState([]);
  const [tempMembers, setTempMembers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [allWorkers, setAllWorkers] = useState([]);
  const [tempQuery, setTempQuery] = useState('');
  const [tempAdds, setTempAdds] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [hasSchedule, setHasSchedule] = useState(false);
  const [reasons, setReasons] = useState({});

  useEffect(() => {
    if (!open || !targetId) return;
    setTempAdds([]);
    setTempQuery('');
    setLoadError(false);
    setReasons({});
    setHasSchedule(false);
    (async () => {
      setLoading(true);
      try {
        const h = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(`${API}/teams/${targetId}/members-details`, { headers: h });
        const mems = data.members || [];
        const temps = data.temp_members || [];
        setMembers(mems);
        setTempMembers(temps);
        if (mode === 'in') {
          let preset = mems.filter((m) => !m.is_clocked_in).map((m) => m.id);
          // Roster from schedule (Aug 2026): pre-check only who's on shift now.
          try {
            const shiftRes = await axios.get(`${API}/teams/${targetId}/on-shift-now`, { headers: h });
            setHasSchedule(!!shiftRes.data.has_schedule);
            if (shiftRes.data.member_ids !== null) {
              const onShift = new Set(shiftRes.data.member_ids);
              preset = preset.filter((id) => onShift.has(id));
            }
          } catch { setHasSchedule(false); }
          setSelected(new Set(preset));
          const res = await axios.get(`${API}/users?scope=all`, { headers: h });
          setAllWorkers(Array.isArray(res.data) ? res.data : []);
        } else {
          setSelected(new Set([
            ...mems.filter((m) => m.is_clocked_in),
            ...temps.filter((t) => t.is_clocked_in),
          ].map((m) => m.id)));
        }
      } catch {
        setMembers([]);
        setTempMembers([]);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, targetId, mode]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const isIn = mode === 'in';
  const eligible = isIn
    ? members.filter((m) => !m.is_clocked_in)
    : [...members.filter((m) => m.is_clocked_in), ...tempMembers.filter((t) => t.is_clocked_in)];
  const offClock = isIn
    ? members.filter((m) => m.is_clocked_in)
    : members.filter((m) => !m.is_clocked_in);

  const rosterIds = new Set([
    ...members.map((m) => m.id),
    ...tempMembers.map((t) => t.id),
    ...tempAdds.map((t) => t.id),
  ]);
  const searchResults = isIn && tempQuery.trim()
    ? allWorkers
        .filter((w) => !rosterIds.has(w.id) && `${w.name || ''} ${w.email || ''}`.toLowerCase().includes(tempQuery.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const confirmCount = selected.size + (isIn ? tempAdds.length : 0);

  const rowBadge = (text, color, bg) => (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ color, background: bg }}>{text}</span>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="team-roster-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isIn ? <Clock className="w-5 h-5" style={{ color: '#00C896' }} /> : <LogOut className="w-5 h-5" style={{ color: '#EF4444' }} />}
            {isIn ? 'Clock In' : 'Clock Out'} — {targetName}
          </DialogTitle>
          <DialogDescription>
            {isIn
              ? 'Uncheck anyone on vacation or a no-show. Only checked members punch in.'
              : 'Only checked members punch out — anyone left checked off stays on the clock.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin inline" style={{ color: '#64748B' }} /></div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-sm font-semibold">Who&apos;s on this shift? ({selected.size}/{eligible.length})</p>
                <div className="flex gap-3 text-xs">
                  <button type="button" className="hover:underline" style={{ color: '#1E88E5' }} data-testid="roster-select-all"
                          onClick={() => setSelected(new Set(eligible.map((m) => m.id)))}>Select all</button>
                  <button type="button" className="hover:underline" style={{ color: '#64748B' }} data-testid="roster-clear-all"
                          onClick={() => setSelected(new Set())}>Clear all</button>
                </div>
              </div>
              {isIn && hasSchedule && (
                <p className="text-xs mb-1" style={{ color: '#1E88E5' }} data-testid="roster-schedule-note">Pre-selected from today&apos;s schedule — adjust as needed.</p>
              )}
              <div className="max-h-52 overflow-y-auto space-y-0.5 rounded-lg p-2" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
                {loadError && (
                  <p className="text-xs py-2 text-center" style={{ color: '#EF4444' }} data-testid="roster-load-error">Couldn&apos;t load the roster. Close and try again.</p>
                )}
                {!loadError && eligible.length === 0 && offClock.length === 0 && (
                  <p className="text-xs py-2 text-center" style={{ color: '#64748B' }}>No members found.</p>
                )}
                {eligible.map((m) => (
                  <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`roster-member-${m.id}`}>
                    <Checkbox checked={selected.has(m.id)} onCheckedChange={() => toggle(m.id)} data-testid={`roster-check-${m.id}`} />
                    <span className="flex-1 text-white">{m.name}</span>
                    {isIn && !selected.has(m.id) && (
                      <select
                        value={reasons[m.id] || ''}
                        onChange={(e) => setReasons((prev) => ({ ...prev, [m.id]: e.target.value }))}
                        onClick={(e) => e.preventDefault()}
                        className="text-xs rounded px-1.5 py-1"
                        style={{ background: '#1A2433', border: '1px solid #2D3748', color: reasons[m.id] ? '#FBBF24' : '#64748B' }}
                        data-testid={`roster-reason-${m.id}`}
                      >
                        <option value="">Reason…</option>
                        <option value="vacation">Vacation</option>
                        <option value="sick">Sick</option>
                        <option value="no_show">No-show</option>
                        <option value="other">Other</option>
                      </select>
                    )}
                    {m.is_temp && rowBadge('Temp', '#A78BFA', 'rgba(167,139,250,0.15)')}
                    {!isIn && m.is_clocked_in && rowBadge('On the clock', '#00C896', 'rgba(0,200,150,0.12)')}
                  </label>
                ))}
                {offClock.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-sm py-1 opacity-60" data-testid={`roster-member-${m.id}`}>
                    <Checkbox checked={false} disabled />
                    <span className="flex-1" style={{ color: '#94A3B8' }}>{m.name}</span>
                    {isIn
                      ? rowBadge('On the clock', '#00C896', 'rgba(0,200,150,0.12)')
                      : rowBadge('Not on the clock', '#64748B', 'rgba(100,116,139,0.15)')}
                  </div>
                ))}
              </div>
            </div>

            {isIn && (
              <div className="space-y-2">
                <p className="text-sm font-semibold flex items-center gap-1.5">
                  <UserPlus className="w-4 h-4" style={{ color: '#A78BFA' }} />
                  Add someone for this shift (mutual / coverage)
                </p>
                <div className="relative">
                  <input
                    type="text"
                    value={tempQuery}
                    onChange={(e) => setTempQuery(e.target.value)}
                    placeholder="Search workers by name…"
                    className="w-full text-sm rounded-lg px-3 py-2 text-white focus:outline-none"
                    style={{ background: '#0F1923', border: '1px solid #2D3748' }}
                    data-testid="roster-temp-search"
                  />
                  {searchResults.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 rounded-lg shadow-lg max-h-44 overflow-y-auto"
                         style={{ background: '#1A2433', border: '1px solid #2D3748' }} data-testid="roster-temp-results">
                      {searchResults.map((w) => (
                        <button key={w.id} type="button"
                                className="w-full px-3 py-2 text-left text-sm text-white hover:bg-[#0F1923]"
                                data-testid={`roster-temp-pick-${w.id}`}
                                onClick={() => { setTempAdds((prev) => [...prev, { id: w.id, name: w.name }]); setTempQuery(''); }}>
                          {w.name} <span className="text-xs" style={{ color: '#64748B' }}>{w.email}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {tempAdds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5" data-testid="roster-temp-adds">
                    {tempAdds.map((t) => (
                      <span key={t.id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                            style={{ color: '#A78BFA', background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.4)' }}
                            data-testid={`roster-temp-chip-${t.id}`}>
                        {t.name}
                        <button type="button" onClick={() => setTempAdds((prev) => prev.filter((x) => x.id !== t.id))}
                                data-testid={`roster-temp-remove-${t.id}`} aria-label={`Remove ${t.name}`}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[11px]" style={{ color: '#64748B' }}>
                  Temps punch in with the team for this shift only — the team roster is unchanged.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => onConfirm({
                  memberIds: [...selected],
                  extraIds: isIn ? tempAdds.map((t) => t.id) : [],
                  absences: isIn
                    ? eligible.filter((m) => !selected.has(m.id) && reasons[m.id]).map((m) => ({ worker_id: m.id, reason: reasons[m.id] }))
                    : [],
                })}
                disabled={busy || confirmCount === 0}
                className="flex-1 text-white"
                style={{ background: isIn ? '#00C896' : '#EF4444' }}
                data-testid="roster-confirm-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {isIn ? `Clock In ${confirmCount}` : `Clock Out ${confirmCount}`}
              </Button>
              <Button onClick={onClose} variant="outline" className="flex-1" data-testid="roster-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
